'use client';

import Link from 'next/link';
import { Play, Check, Lock, Award, Home, Eye, FileText, Globe2, Leaf, Microscope, Calculator, Map, ScrollText, Palette, Music, Heart, Activity, ArrowLeftRight } from 'lucide-react';

interface LearningStoryCardProps {
  id: string;
  title: string;
  coverImageUrl?: string;
  difficultyLevel: number;
  ageCategory: string;
  learningTopic?: string;
  isCompleted?: boolean;
  isLocked?: boolean;
  primaryLanguage: string;
  secondaryLanguage?: string;
  prompt?: string;
}

// Function to get Lucide icon component for each topic
const getTopicIcon = (topic: string, size: number = 16) => {
  const iconProps = { size, color: 'white', strokeWidth: 2.5 };
  
  switch(topic) {
    case 'practical-life': return <Home {...iconProps} />;
    case 'sensorial': return <Eye {...iconProps} />;
    case 'language': return <FileText {...iconProps} />;
    case 'cultural': return <Globe2 {...iconProps} />;
    case 'nature': return <Leaf {...iconProps} />;
    case 'science': return <Microscope {...iconProps} />;
    case 'mathematics': return <Calculator {...iconProps} />;
    case 'geography': return <Map {...iconProps} />;
    case 'history': return <ScrollText {...iconProps} />;
    case 'art': return <Palette {...iconProps} />;
    case 'music': return <Music {...iconProps} />;
    case 'social-emotional': return <Heart {...iconProps} />;
    case 'movement': return <Activity {...iconProps} />;
    default: return <FileText {...iconProps} />;
  }
};

const TOPIC_COLORS: Record<string, string> = {
  'practical-life': '#8B4513',
  'sensorial': '#9370DB',
  'language': '#4169E1',
  'cultural': '#FF6347',
  'nature': '#228B22',
  'science': '#1E90FF',
  'mathematics': '#FF8C00',
  'geography': '#20B2AA',
  'history': '#8B4513',
  'art': '#FF1493',
  'music': '#8A2BE2',
  'social-emotional': '#DC143C',
  'movement': '#FF4500',
};

export default function LearningStoryCard({
  id,
  title,
  coverImageUrl,
  difficultyLevel,
  ageCategory,
  learningTopic = 'language',
  isCompleted = false,
  isLocked = false,
  primaryLanguage,
  secondaryLanguage,
  prompt,
}: LearningStoryCardProps) {
  const topicColor = TOPIC_COLORS[learningTopic] || '#667eea';

  const content = (
    <div
      style={{
        position: 'relative',
        // 2026 glassmorphic design with gradient overlay
        background: isCompleted
          ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(5, 150, 105, 0.02) 100%), rgba(255, 255, 255, 0.95)'
          : 'linear-gradient(135deg, rgba(102, 126, 234, 0.03) 0%, rgba(118, 75, 162, 0.02) 100%), rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        borderRadius: '28px',
        overflow: 'hidden',
        cursor: isLocked ? 'not-allowed' : 'pointer',
        transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
        border: isCompleted 
          ? '2px solid rgba(16, 185, 129, 0.4)' 
          : '2px solid rgba(255, 255, 255, 0.6)',
        boxShadow: isCompleted
          ? '0 16px 56px rgba(16, 185, 129, 0.25), 0 8px 24px rgba(16, 185, 129, 0.15), 0 0 0 1px rgba(16, 185, 129, 0.1) inset'
          : '0 16px 48px rgba(102, 126, 234, 0.15), 0 8px 24px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(255, 255, 255, 0.4) inset',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        opacity: isLocked ? 0.6 : 1,
      }}
      className="learning-story-card group"
      onMouseEnter={(e) => {
        if (!isLocked) {
          e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)';
          e.currentTarget.style.boxShadow = isCompleted
            ? '0 24px 72px rgba(16, 185, 129, 0.35), 0 12px 32px rgba(16, 185, 129, 0.2)'
            : '0 24px 64px rgba(102, 126, 234, 0.25), 0 12px 32px rgba(0, 0, 0, 0.12)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0) scale(1)';
        e.currentTarget.style.boxShadow = isCompleted
          ? '0 16px 56px rgba(16, 185, 129, 0.25), 0 8px 24px rgba(16, 185, 129, 0.15)'
          : '0 16px 48px rgba(102, 126, 234, 0.15), 0 8px 24px rgba(0, 0, 0, 0.08)';
      }}
    >
      {/* Cover Image */}
      <div
        style={{
          position: 'relative',
          paddingTop: '140%',
          background: coverImageUrl
            ? `url(${coverImageUrl}) center/cover`
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          overflow: 'hidden',
        }}
      >
        {/* Completed Badge - Enhanced */}
        {isCompleted && (
          <div
            style={{
              position: 'absolute',
              top: 'clamp(12px, 2vw, 16px)',
              right: 'clamp(12px, 2vw, 16px)',
              background: 'linear-gradient(135deg, #10b981, #059669)',
              borderRadius: '50%',
              width: 'clamp(44px, 6vw, 52px)',
              height: 'clamp(44px, 6vw, 52px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 24px rgba(16, 185, 129, 0.5), 0 0 0 3px rgba(255, 255, 255, 0.8)',
              zIndex: 2,
              animation: 'pulse 2s ease-in-out infinite',
            }}
          >
            <Check size={24} color="white" strokeWidth={3.5} />
          </div>
        )}

        {/* Level Badge - Enhanced */}
        <div
          style={{
            position: 'absolute',
            top: 'clamp(12px, 2vw, 16px)',
            left: 'clamp(12px, 2vw, 16px)',
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(15px)',
            WebkitBackdropFilter: 'blur(15px)',
            padding: 'clamp(0.5rem, 1.5vw, 0.625rem) clamp(0.75rem, 2vw, 1rem)',
            borderRadius: '14px',
            fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
            fontWeight: 800,
            color: '#B4A0E5',
            display: 'flex',
            alignItems: 'center',
            gap: 'clamp(0.375rem, 1vw, 0.5rem)',
            boxShadow: '0 4px 12px rgba(180, 160, 229, 0.3), 0 0 0 2px rgba(255, 255, 255, 0.8)',
            zIndex: 2,
            border: '1px solid rgba(180, 160, 229, 0.3)',
          }}
        >
          <Award size={16} strokeWidth={2.5} />
          Level {difficultyLevel}
        </div>

        {/* Topic Badge - Enhanced */}
        <div
          style={{
            position: 'absolute',
            bottom: 'clamp(12px, 2vw, 16px)',
            left: 'clamp(12px, 2vw, 16px)',
            background: topicColor,
            padding: 'clamp(0.5rem, 1.5vw, 0.625rem) clamp(0.75rem, 2vw, 1rem)',
            borderRadius: '14px',
            fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
            fontWeight: 800,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 'clamp(0.375rem, 1vw, 0.5rem)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3), 0 0 0 2px rgba(255, 255, 255, 0.8)',
            zIndex: 2,
            textTransform: 'capitalize',
          }}
        >
          {getTopicIcon(learningTopic, 18)}
          {learningTopic.replace(/-/g, ' ')}
        </div>

        {/* Play Overlay - Enhanced */}
        {!isLocked && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: isCompleted
                ? 'rgba(16, 185, 129, 0.25)'
                : 'rgba(180, 160, 229, 0.4)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              opacity: 0,
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
            className="play-overlay"
          >
            <div
              style={{
                width: 'clamp(70px, 10vw, 80px)',
                height: 'clamp(70px, 10vw, 80px)',
                background: 'linear-gradient(135deg, #FFFFFF, #F8FAFC)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 4px rgba(255, 255, 255, 0.8)',
                animation: 'pulse 2s ease-in-out infinite',
              }}
            >
              <Play
                size={36}
                color={isCompleted ? '#10b981' : '#B4A0E5'}
                fill={isCompleted ? '#10b981' : '#B4A0E5'}
                style={{ marginLeft: '5px' }}
                strokeWidth={0}
              />
            </div>
          </div>
        )}

        {/* Locked Overlay */}
        {isLocked && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(0, 0, 0, 0.6)',
              backdropFilter: 'blur(2px)',
            }}
          >
            <Lock size={40} color="white" />
          </div>
        )}
      </div>

      {/* Book Info */}
      <div style={{ padding: '16px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <h3
          style={{
            color: '#1a202c',
            fontSize: '1.125rem',
            fontWeight: 800,
            margin: '0 0 8px 0',
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {title}
        </h3>

        {prompt && (
          <p
            style={{
              color: '#64748b',
              fontSize: '0.875rem',
              margin: '0 0 12px 0',
              lineHeight: 1.5,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              flex: 1,
            }}
          >
            {prompt}
          </p>
        )}

        {/* Languages - Enhanced */}
        {secondaryLanguage && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'clamp(0.5rem, 1vw, 0.625rem)',
              fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
              color: '#64748b',
              fontWeight: 700,
              marginTop: 'auto',
            }}
          >
            <div
              style={{
                background: 'linear-gradient(135deg, #E0F2FE, #BAE6FD)',
                padding: 'clamp(0.375rem, 1vw, 0.5rem) clamp(0.625rem, 1.5vw, 0.875rem)',
                borderRadius: '10px',
                color: '#0369A1',
                border: '1.5px solid rgba(168, 216, 230, 0.4)',
                boxShadow: '0 2px 6px rgba(168, 216, 230, 0.2)',
              }}
            >
              {primaryLanguage.toUpperCase()}
            </div>
            <div style={{
              background: 'linear-gradient(135deg, #B4A0E5, #A8D8E6)',
              borderRadius: '50%',
              width: 'clamp(20px, 3vw, 24px)',
              height: 'clamp(20px, 3vw, 24px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <ArrowLeftRight size={14} color="white" strokeWidth={2.5} />
            </div>
            <div
              style={{
                background: 'linear-gradient(135deg, #FFF5EB, #FEF3E2)',
                padding: 'clamp(0.375rem, 1vw, 0.5rem) clamp(0.625rem, 1.5vw, 0.875rem)',
                borderRadius: '10px',
                color: '#92400E',
                border: '1.5px solid rgba(248, 200, 160, 0.4)',
                boxShadow: '0 2px 6px rgba(248, 200, 160, 0.2)',
              }}
            >
              {secondaryLanguage.toUpperCase()}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (isLocked) {
    return (
      <div style={{ minWidth: '280px', flex: '0 0 280px' }}>
        {content}
        <style jsx global>{`
          .learning-story-card .play-overlay {
            opacity: 0 !important;
          }
        `}</style>
      </div>
    );
  }

  return (
    <Link
      href={`/audio-book/${id}`}
      style={{
        minWidth: '280px',
        textDecoration: 'none',
        flex: '0 0 280px',
      }}
    >
      {content}
      <style jsx global>{`
        .learning-story-card:hover {
          transform: translateY(-12px) scale(1.03) rotate(0.5deg);
          box-shadow: 0 30px 80px rgba(180, 160, 229, 0.3) !important;
        }
        .learning-story-card:hover .play-overlay {
          opacity: 1 !important;
        }
        
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.05);
            opacity: 0.9;
          }
        }
        
        @media (max-width: 768px) {
          .learning-story-card {
            border-radius: 24px !important;
          }
          .learning-story-card:hover {
            transform: translateY(-6px) scale(1.02) !important;
          }
        }
      `}</style>
    </Link>
  );
}

